/**
 * Outil « Fonctions de référence » — définitions (pur).
 *
 * Carré, inverse, racine carrée, cube, valeur absolue : ensemble de
 * définition, parité, morceaux où la fonction est monotone (pour remplir le
 * tableau de variations), fenêtre d'affichage, et image EXACTE d'un x
 * multiple de 0,5 (fractions, racines).
 */

import { rational, type ExactNumber } from '../../ui/exact';
import type { Viewport } from '../../ui/frame';
import { isqrt, simplify } from '../root-square/roots';

export type FnId = 'square' | 'inverse' | 'sqrt' | 'cube' | 'abs';

export interface Piece {
  /** bornes du morceau (±Infinity pour ±∞) */
  from: number;
  to: number;
  dir: 'up' | 'down';
  /** intervalle écrit : « ]−∞ ; 0] » */
  text: string;
}

export interface RefFn {
  id: FnId;
  /** onglet : « x² » */
  label: string;
  name: string;
  /** f(x) en LaTeX */
  tex: string;
  f: (x: number) => number;
  defined: (x: number) => boolean;
  domain: string;
  parity: 'even' | 'odd' | 'none';
  vp: Viewport;
  /** bornes de x pour le point M (dans la fenêtre) */
  xMin: number;
  xMax: number;
  pieces: Piece[];
  /** colonnes du tableau de variations : x écrits, valeur de f (ou null), double barre */
  table: Array<{ x: string; fx: string | null; bar?: boolean }>;
  order: string;
}

export const FUNCTIONS: readonly RefFn[] = [
  {
    id: 'square',
    label: 'x²',
    name: 'fonction carré',
    tex: 'x^2',
    f: (x) => x * x,
    defined: () => true,
    domain: 'ℝ',
    parity: 'even',
    vp: { xMin: -3.6, xMax: 3.6, yMin: -1.2, yMax: 10.5 },
    xMin: -3,
    xMax: 3,
    pieces: [
      { from: -Infinity, to: 0, dir: 'down', text: ']−∞ ; 0]' },
      { from: 0, to: Infinity, dir: 'up', text: '[0 ; +∞[' },
    ],
    table: [
      { x: '−∞', fx: null },
      { x: '0', fx: '0' },
      { x: '+∞', fx: null },
    ],
    order:
      'Sur [0 ; +∞[, la fonction carré est croissante : si 0 ≤ a ≤ b, alors a² ≤ b². Sur ]−∞ ; 0], elle est décroissante : si a ≤ b ≤ 0, alors a² ≥ b² (l’ordre s’inverse).',
  },
  {
    id: 'inverse',
    label: '1/x',
    name: 'fonction inverse',
    tex: '\\frac{1}{x}',
    f: (x) => 1 / x,
    defined: (x) => x !== 0,
    domain: ']−∞ ; 0[ ∪ ]0 ; +∞[ (ℝ privé de 0)',
    parity: 'odd',
    vp: { xMin: -4.4, xMax: 4.4, yMin: -4.4, yMax: 4.4 },
    xMin: -4,
    xMax: 4,
    pieces: [
      { from: -Infinity, to: 0, dir: 'down', text: ']−∞ ; 0[' },
      { from: 0, to: Infinity, dir: 'down', text: ']0 ; +∞[' },
    ],
    table: [
      { x: '−∞', fx: null },
      { x: '0', fx: null, bar: true },
      { x: '+∞', fx: null },
    ],
    order:
      'Décroissante sur ]−∞ ; 0[ et sur ]0 ; +∞[, mais PAS sur les deux à la fois : −1 < 1 et pourtant 1/(−1) = −1 < 1 = 1/1. 0 n’a pas d’image.',
  },
  {
    id: 'sqrt',
    label: '√x',
    name: 'fonction racine carrée',
    tex: '\\sqrt{x}',
    f: (x) => Math.sqrt(x),
    defined: (x) => x >= 0,
    domain: '[0 ; +∞[',
    parity: 'none',
    vp: { xMin: -1, xMax: 10, yMin: -1, yMax: 4 },
    xMin: 0,
    xMax: 9,
    pieces: [{ from: 0, to: Infinity, dir: 'up', text: '[0 ; +∞[' }],
    table: [
      { x: '0', fx: '0' },
      { x: '+∞', fx: null },
    ],
    order:
      'Croissante sur [0 ; +∞[ : si 0 ≤ a ≤ b, alors √a ≤ √b. Un nombre négatif n’a pas de racine carrée.',
  },
  {
    id: 'cube',
    label: 'x³',
    name: 'fonction cube',
    tex: 'x^3',
    f: (x) => x * x * x,
    defined: () => true,
    domain: 'ℝ',
    parity: 'odd',
    vp: { xMin: -2.4, xMax: 2.4, yMin: -9, yMax: 9 },
    xMin: -2,
    xMax: 2,
    pieces: [{ from: -Infinity, to: Infinity, dir: 'up', text: 'ℝ' }],
    table: [
      { x: '−∞', fx: null },
      { x: '+∞', fx: null },
    ],
    order: 'Croissante sur ℝ : si a ≤ b, alors a³ ≤ b³. Le cube garde le signe : (−2)³ = −8.',
  },
  {
    id: 'abs',
    label: '|x|',
    name: 'fonction valeur absolue',
    tex: '|x|',
    f: (x) => Math.abs(x),
    defined: () => true,
    domain: 'ℝ',
    parity: 'even',
    vp: { xMin: -4.4, xMax: 4.4, yMin: -1, yMax: 5 },
    xMin: -4,
    xMax: 4,
    pieces: [
      { from: -Infinity, to: 0, dir: 'down', text: ']−∞ ; 0]' },
      { from: 0, to: Infinity, dir: 'up', text: '[0 ; +∞[' },
    ],
    table: [
      { x: '−∞', fx: null },
      { x: '0', fx: '0' },
      { x: '+∞', fx: null },
    ],
    order:
      '|x| est la distance entre x et 0 : décroissante sur ]−∞ ; 0], croissante sur [0 ; +∞[ ; |−3| = |3| = 3.',
  },
];

export function fnById(id: FnId): RefFn {
  return FUNCTIONS.find((f) => f.id === id)!;
}

/** Indices des morceaux qui contiennent x (un point frontière est dans les deux). */
export function piecesAt(fn: RefFn, x: number): number[] {
  return fn.pieces
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => x >= p.from && x <= p.to && fn.defined(x))
    .map(({ i }) => i);
}

/**
 * Morceaux révélés quand le point passe de `a` à `b` : ceux qui contiennent
 * les deux positions (le sens de variation y est observé).
 */
export function revealed(fn: RefFn, a: number, b: number): number[] {
  if (a === b) return [];
  const pa = piecesAt(fn, a);
  return piecesAt(fn, b).filter((i) => pa.includes(i));
}

const SQRT_SYM = '√';

/** f(x) exact pour x multiple de 0,5 : décimal, fraction ou racine (√2, √2,5). */
export function exactImage(fn: RefFn, x: number): ExactNumber {
  const n = Math.round(x * 2); // x = n / 2
  switch (fn.id) {
    case 'square':
      return rational(n * n, 4);
    case 'inverse':
      return rational(2, n);
    case 'cube':
      return rational(n * n * n, 8);
    case 'abs':
      return rational(Math.abs(n), 2);
    case 'sqrt': {
      // x = n/2 : √x = √(2n) / 2, simplifiée en k√b / 2 (√8 = 2√2, √4,5 = 3√2/2)
      const m = n % 2 === 0 ? n / 2 : 2 * n;
      const den = n % 2 === 0 ? 1 : 2;
      const r = isqrt(m);
      if (r * r === m) return rational(r, den);
      const { k, b } = simplify(m);
      const kk = k === 1 ? '' : String(k);
      const root = `${kk}${SQRT_SYM}${b}`;
      const rootTex = `${kk}\\sqrt{${b}}`;
      const rootAtoms = [...(k === 1 ? [] : [String(k)]), '√', String(b)];
      return den === 1
        ? { plain: root, tex: rootTex, atoms: rootAtoms, value: Math.sqrt(x) }
        : {
            plain: `${root}/2`,
            tex: `\\frac{${rootTex}}{2}`,
            atoms: [...rootAtoms, '÷', '2'],
            value: Math.sqrt(x),
          };
    }
  }
}
